import { formatInteger } from '@luminovo/frontend-utils';
import { Tab as MuiTab, TabProps as MuiTabProps, styled } from '@mui/material';
import React from 'react';

import { linkable } from '../../routing/links/linkable';
import { colorSystem, h5 } from '../../theme';
import { Flexbox } from '../Flexbox';
import type { IconElement } from '../icons/IconElement';

type TabProps = Omit<MuiTabProps, 'icon'> & {
    icon?: IconElement;
    count?: number;
};

const DefaultTab = styled(MuiTab)({
    color: colorSystem.neutral[6],
    minWidth: 'fit-content',
    width: 'fit-content',
    minHeight: 'auto',
    '&:hover': { color: colorSystem.neutral[6] },
    '&.Mui-selected': {
        color: colorSystem.neutral[9],
    },
    '& .MuiTab-labelIcon': {
        minHeight: 0,
    },
});

const TabHost = React.forwardRef<HTMLDivElement, TabProps>(
    ({ icon, count, label, ...props }: TabProps, ref): JSX.Element => {
        const tabLabel = (
            <Flexbox
                gap={4}
                sx={{
                    alignItems: 'center',
                }}
            >
                {icon}
                {label}
                {count !== undefined ? (
                    <div
                        style={{
                            ...h5,
                            background: colorSystem.neutral[2],
                            borderRadius: '8px',
                            padding: '0px 4px',
                            height: '16px',
                            alignItems: 'center',
                            lineHeight: '16px',
                        }}
                    >
                        {formatInteger(count)}
                    </div>
                ) : null}
            </Flexbox>
        );

        return <DefaultTab {...props} ref={ref} label={tabLabel} disableTouchRipple />;
    },
);

/** A tab; with `to` it's a real `<a role="tab">`, and inside `Tabs`, `onChange` still fires on click. */
export const Tab = linkable(TabHost);
